import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import type { MigrationStepStatus } from "./MigrationStepper";

interface Props<K extends string> {
  currentStep: K;
  steps: { key: K; status: MigrationStepStatus }[];
  onBack: () => void;
  onContinue: () => void;
  /** When provided, supersedes the internal status-based gate so the parent
   *  can drive Continue from blockers (missing fields, parse errors). */
  disabled?: boolean;
  /** Tooltip body shown on hover when `disabled` is true. */
  disabledReason?: string;
  /** Override label, e.g. "Confirm & continue". Defaults to "Continue". */
  continueLabel?: string;
  // Optional copy for a passive last-step (badge when complete, muted text
  // when pending). Provide BOTH to opt into the passive treatment — used by
  // wizards where the final step has no Continue action (e.g. bulk import's
  // "review" step). When omitted, the Continue button shows on the last
  // step like every other step.
  lastStepCompleteLabel?: string;
  lastStepPendingLabel?: string;
}

/**
 * Unified footer below the wizard's step body. Variant A spec:
 *   [Back ←]       Step N of N       [Continue →]
 *
 * Continue is disabled until the current step's status is `confirmed` (or
 * `skipped`), unless the parent supplies its own `disabled` value — in which
 * case the parent owns the gate and is expected to confirm-on-continue itself.
 */
export function StepFooter<K extends string>({
  currentStep,
  steps,
  onBack,
  onContinue,
  disabled,
  disabledReason,
  continueLabel = "Continue",
  lastStepCompleteLabel,
  lastStepPendingLabel,
}: Props<K>) {
  const idx = steps.findIndex((s) => s.key === currentStep);
  const totalSteps = steps.length;
  const isFirst = idx <= 0;
  const isLast = idx === totalSteps - 1;
  const currentStatus = steps[idx]?.status ?? "pending";
  // Skippable steps land on "skipped" — treat that the same as confirmed
  // for the purpose of advancing through the wizard.
  const internalCanAdvance =
    currentStatus === "confirmed" || currentStatus === "skipped";
  // Parent-driven gate takes priority when provided.
  const isDisabled =
    disabled !== undefined ? disabled : !internalCanAdvance;

  const continueButton = (
    <Button size="sm" onClick={onContinue} disabled={isDisabled}>
      {continueLabel}
      <ArrowRight className="ml-1 h-3.5 w-3.5" />
    </Button>
  );

  return (
    <div className="flex items-center gap-2 rounded-md border bg-stone-50 px-4 py-3 dark:bg-stone-900/40">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={isFirst}
      >
        <ArrowLeft className="mr-1 h-3.5 w-3.5" />
        Back
      </Button>
      <div className="flex-1 text-center text-xs text-muted-foreground">
        Step {idx + 1} of {totalSteps}
      </div>
      {isLast && lastStepCompleteLabel && lastStepPendingLabel ? (
        internalCanAdvance ? (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            <Check className="mr-1 h-3 w-3" />
            {lastStepCompleteLabel}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">
            {lastStepPendingLabel}
          </span>
        )
      ) : isDisabled && disabledReason ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block cursor-not-allowed">
                {continueButton}
              </span>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        continueButton
      )}
    </div>
  );
}
